import React, { useState, useRef, useEffect } from 'react';
import { Modal, Tabs, Form, Input, notification, Spin, Tooltip } from 'antd';
import { XCircleIcon, XIcon } from '@heroicons/react/solid';
import words from '../../../services/words';
import Errors from '../../../components/errors';

const CreateOrEditEssences = ({
  visible = true,
  selectedTab = 'Honestidad',
  essences = [],
  onCancel,
  onEssenceChange,
}) => {
  const [init, setInit] = useState(false);
  const [activeKey, setActiveKey] = useState(selectedTab);
  const [getWordTask, setGetWordTask] = useState({ loading: false, id: null });
  const formRefs = useRef({});

  useEffect(() => {
    if (visible && !init) {
      initWords().then((_) => setInit(true));
    }
    // eslint-disable-next-line
  }, [visible]);

  useEffect(() => {
    setActiveKey(selectedTab);
  }, [selectedTab]);

  const initWords = async () => {
    try {
      for (const formRef of Object.values(formRefs.current)) {
        for (const essence of essences) {
          for (const word of essence.words) {
            formRef.setFieldsValue({ [word.origin]: word.word });
          }
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getWord = async (e, essence) => {
    try {
      const query = e.target.value.trim();

      if (!query) return;
      setGetWordTask({
        loading: true,
        id: e.target.id,
      });

      const { word } = await words.getWord(query);

      const index = essence.words.findIndex((item) => item.word === word.word);

      if (index >= 0) {
        notification.open({
          type: 'error',
          message: 'No se permite repetir las palabras',
          placement: 'bottomRight',
        });

        setGetWordTask({
          loading: false,
          id: null,
        });

        return removeWord(essence, e.target.id);
      }

      essence.words.push({ ...word, origin: e.target.id });

      onEssenceChange(essence);

      formRefs.current[essence.name].setFieldsValue({
        [e.target.id]: word.word,
      });

      setTimeout(() => {
        setGetWordTask({
          loading: false,
          id: null,
        });
      }, 500);
    } catch (error) {
      setGetWordTask({
        loading: false,
        id: null,
      });
      notification.open({
        type: 'error',
        message: error.message || 'Ocurió un error',
        description: <Errors errors={error?.errors} />,
        placement: 'bottomRight',
      });
    }
  };

  function disabledInputValue(essence, field) {
    const { loading, id } = getWordTask;

    const inputName = `${essence.name}-${field}`;
    const origin = essence.words.find(({ origin }) => origin === inputName);

    if ((loading && inputName === id) || origin) {
      return true;
    }
  }

  function loadingInputValue(essence, field) {
    const { loading, id } = getWordTask;
    const inputName = `${essence.name}-${field}`;

    if (loading && inputName === id) {
      return <Spin size="small" className="absolute top-2 right-2" />;
    }
  }

  function inputClearControl(essence, field) {
    const inputName = `${essence.name}-${field}`;
    if (getWordTask.loading && getWordTask.id === inputName) return;

    const origin = essence.words.find(({ origin }) => origin === inputName);

    if (origin) {
      return (
        <Tooltip
          title="Limpiar"
          className="absolute top-1 right-2"
          onClick={() => removeWord(essence, field)}
        >
          <XCircleIcon
            width={22}
            className="text-red-400 hover:text-red-500 cursor-pointer"
          />
        </Tooltip>
      );
    }
  }

  const removeWord = (essence, field) => {
    try {
      const inputName = `${essence.name}-${field}`;

      const value = formRefs.current[essence.name].getFieldValue(inputName);

      const index = essence.words.findIndex(
        ({ word, origin }) => word === value && origin === inputName
      );

      if (index >= 0) {
        essence.words.splice(index, 1);

        onEssenceChange(essence);
      }

      formRefs.current[essence.name].resetFields([inputName]);
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <Modal
      visible={visible}
      title={null}
      footer={null}
      closable={false}
      width={600}
      centered
      onCancel={() => onCancel()}
    >
      <div
        className="absolute top-3 right-3 cursor-pointer"
        onClick={() => onCancel()}
      >
        <XIcon width={20} />
      </div>
      <Tabs
        activeKey={activeKey}
        onTabClick={(activeKey) => setActiveKey(activeKey)}
      >
        {essences.map((essence, index) => (
          <Tabs.TabPane
            forceRender={true}
            className="py-4"
            tab={
              <div className="relative mt-2">
                <div className="text-lg" style={{ color: essence.color }}>
                  {essence.name}
                </div>
                <div className="absolute -top-4 right-0 text-xs font-medium text-white bg-cyan-500 px-1 rounded-md">
                  {parseFloat(essence.value).toFixed(1)}
                </div>
              </div>
            }
            key={essence.name}
          >
            <div className="mb-2">
              Ingrese las palabras para calcular la esencia del usuario.
            </div>
            <Form ref={(ref) => (formRefs.current[essence.name] = ref)}>
              <div className={`grid grid-cols-3 gap-2`}>
                <div className="relative">
                  <Form.Item
                    name={`${essence.name}-word1`}
                    rules={[{ required: true, message: 'Ingrese el campo' }]}
                  >
                    <Input
                      placeholder="Referencia"
                      onBlur={(e) => getWord(e, essence)}
                      disabled={disabledInputValue(essence, 'word1')}
                    />
                  </Form.Item>
                  {loadingInputValue(essence, 'word1')}
                  {inputClearControl(essence, 'word1')}
                </div>
                <div className="relative">
                  <Form.Item
                    name={`${essence.name}-word2`}
                    rules={[{ required: true, message: 'Ingrese el campo' }]}
                    className="relative"
                  >
                    <Input
                      placeholder="5 estrellas"
                      onBlur={(e) => getWord(e, essence)}
                      disabled={disabledInputValue(essence, 'word2')}
                      suffix={
                        loadingInputValue(essence, 'word2') && (
                          <Spin size="small" />
                        )
                      }
                    />
                  </Form.Item>
                  {inputClearControl(essence, 'word2')}
                </div>
                <div className="relative">
                  <Form.Item
                    name={`${essence.name}-word3`}
                    rules={[{ required: true, message: 'Ingrese el campo' }]}
                    className="relative"
                  >
                    <Input
                      placeholder="Amigo"
                      onBlur={(e) => getWord(e, essence)}
                      disabled={disabledInputValue(essence, 'word3')}
                      suffix={
                        loadingInputValue(essence, 'word3') && (
                          <Spin size="small" />
                        )
                      }
                    />
                  </Form.Item>
                  {inputClearControl(essence, 'word3')}
                </div>
              </div>
            </Form>
            {/* <div
              className="relative left-2"
              style={new Essence(essence).style}
            /> */}
          </Tabs.TabPane>
        ))}
      </Tabs>
    </Modal>
  );
};

export default CreateOrEditEssences;
